import CheckIcon from '@mui/icons-material/Check';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  alpha,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { authContext, Profile } from '@staffedupusa/shared-components';
import JSZip from 'jszip';
import { Dispatch, FC, SetStateAction, useContext, useState } from 'react';

import {
  downloadEmployerResumeRoute,
  downloadResumeRoute,
} from '@features/applicant';
import { Application } from '@features/applications';
import { white } from '@styles/colors';
import axios from 'axios';
import { getCookie } from '@utils/getCookie';
import { LoadingButton } from '@mui/lab';
import { track } from '@amplitude/analytics-browser';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import { selectedPlanName } from '@utils/pricing';

type ApplicationViewModalHeaderProps = {
  applicantApplicationsData: Application[];
  application: Application | null;
  setApplication: Dispatch<SetStateAction<Application | null>>;
  applicantDetailsData: Profile | null;
};

const MAX_VISIBLE_APPLICATIONS = 2;

const ApplicationViewModalHeader: FC<ApplicationViewModalHeaderProps> = ({
  applicantApplicationsData,
  application,
  setApplication,
  applicantDetailsData,
}) => {
  const { authData } = useContext(authContext);
  const isCompany = authData.user?.company;
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);
  const theme = useTheme();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const handleDownloadApplication = async () => {
    isCompany &&
      track(
        `${amplitudeEventsName.APPLICANTS_CLICK_DOWNLOAD_APPLICATION}-${selectedPlan}`,
      );
    setDownloadLoading(true);
    const zip = new JSZip();

    const resumeLink = applicantDetailsData?.resume_path;
    const applicationInfoLink = `${import.meta.env.VITE_API_BASE_URL}${downloadEmployerResumeRoute}?user_id=${application?.applicant?.id}&application_id=${application?.id}`;

    const extractFileExtension = (url: string) => {
      return url.split('.').pop();
    };

    const token = getCookie('SU_AUTH');
    const crsfToken = getCookie('csrftoken');

    const fetchFileAsBlob = async (url: string) => {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Token ${token}`,
          'X-CSRFToken': crsfToken,
        },
        responseType: 'blob',
      });
      return response.data;
    };

    const addFileToZip = async (
      url: string,
      filename: string,
      resume: boolean,
    ) => {
      try {
        if (resume) {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch ${url}`);
          }
          const blob = await response.blob();
          zip.file(filename, blob);
        } else {
          const fileBlob = await fetchFileAsBlob(url);
          zip.file(filename, fileBlob);
        }
      } catch (error) {
        console.error(`Error fetching or adding file to zip: ${error}`);
      }
    };

    if (resumeLink) {
      // Extract the file extension from the resumeLink
      const resumeExtension = extractFileExtension(resumeLink);
      const resumeFilename = `resume.${resumeExtension}`;
      await addFileToZip(resumeLink, resumeFilename, true);
    }

    if (applicationInfoLink) {
      const applicationInfoFilename = 'application_info.pdf';
      await addFileToZip(applicationInfoLink, applicationInfoFilename, false);
    }

    zip.generateAsync({ type: 'blob' }).then((content) => {
      const zipFile = new Blob([content]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipFile);
      link.download = `${application?.applicant?.name}-application.zip`;
      link.click();
    });
    setDownloadLoading(false);
  };

  const slicedApplications = applicantApplicationsData?.filter(
    (item, index) => {
      const sliceLength = MAX_VISIBLE_APPLICATIONS;
      return index < sliceLength;
    },
  );

  return (
    <Grid container spacing={2}>
      <Grid item lg={7} md={6} xs={12} bgcolor={white} p={2} borderRadius="8px">
        <Stack
          spacing={2}
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          justifyContent={{ xs: 'center', md: 'flex-start' }}
          sx={{ width: '100%', overflowX: 'auto', height: '100%' }}
        >
          <Typography variant="h4" color="secondary" textTransform="uppercase">
            Applications:
          </Typography>
          {slicedApplications.map((item) => {
            const isSelected = item.id === application?.id;
            const baseColor = theme.palette.secondary.main;

            return (
              <Button
                key={item.id}
                variant="outlined"
                onClick={() => setApplication(item)}
                size="small"
                sx={{
                  minWidth: 'fit-content',
                  fontSize: '14px',
                  lineHeight: '18px',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  backgroundColor: isSelected
                    ? alpha(baseColor, 0.2)
                    : 'transparent',
                  color: isSelected ? baseColor : theme.palette.text.primary,
                  border: isSelected
                    ? 'none'
                    : `1px solid ${theme.palette.grey[500]}`,
                  '&:hover': {
                    backgroundColor: isSelected
                      ? alpha(baseColor, 0.3)
                      : theme.palette.action.hover,
                  },
                }}
              >
                {item.position}
              </Button>
            );
          })}
          {applicantApplicationsData.length > MAX_VISIBLE_APPLICATIONS && (
            <FormControl sx={{ minWidth: 120 }}>
              <Select
                displayEmpty
                onChange={(e) =>
                  setApplication(
                    applicantApplicationsData.find(
                      ({ id }) => id === Number(e.target.value),
                    )!,
                  )
                }
                size="small"
                fullWidth
                value={
                  applicantApplicationsData
                    .slice(MAX_VISIBLE_APPLICATIONS)
                    .some(({ id }) => id === Number(application?.id))
                    ? application?.id
                    : ''
                }
                sx={{
                  borderRadius: 5,
                  fontSize: '14px',
                  lineHeight: '18px',
                  minHeight: 'unset',
                  height: '28px',
                  paddingTop: '5px',
                  paddingBottom: '5px',
                }}
                MenuProps={{ PaperProps: { style: { borderRadius: 5 } } }}
              >
                <MenuItem value="" sx={{ display: 'none' }}>
                  Others
                </MenuItem>
                {applicantApplicationsData
                  .slice(MAX_VISIBLE_APPLICATIONS)
                  .map((item) => (
                    <MenuItem value={item.id} key={item.id}>
                      {item.position}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Grid>
      <Grid item lg={5} md={6} xs={12}>
        <Stack
          spacing={1}
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="center"
          alignItems="center"
        >
          <Button
            variant="contained"
            size="small"
            color="green"
            startIcon={<CheckIcon />}
            onClick={() => {
              isCompany &&
                track(
                  `${amplitudeEventsName.APPLICANTS_CLICK_VERIFY}-${selectedPlan}`,
                );
            }}
          >
            <Typography
              fontSize="14px"
              component="span"
              lineHeight="1.1"
              sx={{ maxWidth: '50px', mr: 1 }}
            >
              Screen with
            </Typography>
            <Typography component="span" variant="h6" lineHeight="1.1">
              Verified First
            </Typography>
          </Button>

          <div id="vf-widget-button-wrap" />
          <LoadingButton
            variant="contained"
            size="small"
            color="secondary"
            loading={downloadLoading}
            startIcon={<FileDownloadIcon />}
            sx={{ fontSize: '15px' }}
            style={{ lineHeight: '31px' }}
            onClick={handleDownloadApplication}
          >
            Download Application
          </LoadingButton>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default ApplicationViewModalHeader;
